'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Bell, CheckCircle2, XCircle, Pause, Info, RefreshCw, X, Settings as SettingsIcon } from 'lucide-react';
import clsx from 'clsx';
import { getNotifications, markNotificationRead, markAllNotificationsRead } from '@/lib/api';
import { Notification } from '@/types';
import PlanToggle from '@/components/ui/PlanToggle';
import BugReportButton from '@/components/BugReportButton';
import ManualSyncButton from '@/components/ManualSyncButton';

// Right-side action cluster: PlanToggle (admin) + dark-mode toggle +
// notification bell with dropdown. Used by both the desktop Header (sticky
// strip inside <main>) and the mobile topbar (inside Sidebar's mobile strip).
// State (notifications, dropdown open) lives here so each mount instance
// manages its own — desktop and mobile never render simultaneously, so this
// is fine.

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const NOTIF_ICONS: Record<string, React.ElementType> = {
  'workflow-complete': CheckCircle2,
  'workflow-failed': XCircle,
  'workflow-paused': Pause,
  sync: RefreshCw,
  system: Info,
};

const NOTIF_COLORS: Record<string, string> = {
  'workflow-complete': 'text-[#00C2A8]',
  'workflow-failed': 'text-red-500',
  'workflow-paused': 'text-orange-500',
  sync: 'text-blue-500',
  system: 'text-gray-500',
};

export default function HeaderActions() {
  const router = useRouter();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const data = await getNotifications();
    setNotifications(data);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const unread = notifications.filter(n => !n.read).length;

  const handleMarkAllRead = async () => {
    await markAllNotificationsRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleNotifClick = async (notif: Notification) => {
    if (!notif.read) {
      await markNotificationRead(notif.id);
      setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    }
    setOpen(false);
    router.push(notif.link);
  };

  return (
    <div className="flex items-center gap-1">
      <PlanToggle />

      {/* Settings gear — mobile only, since Settings isn't a tab on mobile.
          Desktop has Settings in the sidebar nav so we hide the gear there. */}
      <Link
        href="/settings"
        className="md:hidden p-2 rounded-lg hover:bg-[--bg-main] text-[--text-secondary] hover:text-[--text-primary] transition-colors"
        aria-label="Settings"
      >
        <SettingsIcon className="w-4 h-4" />
      </Link>

      {/* Manual CRM refresh — sits between Settings and Bug Report so
          operators reach for it the same way they do on Copilot's own
          mobile app. Rate-limited server-side to 1 click / 30s / tenant. */}
      <ManualSyncButton />

      {/* Bug report trigger — every signed-in user, every page, sits next
          to the bell so reports are one click away from where the issue
          surfaces. Theme toggle was previously here but moved to
          Settings → Appearance so the header stays focused. */}
      <BugReportButton variant="header" />

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setOpen(!open)}
          className="relative p-2 rounded-lg hover:bg-[--bg-main] text-[--text-secondary] hover:text-[--text-primary] transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {unread > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-1 w-[calc(100vw-2rem)] max-w-[24rem] sm:w-96 bg-[--bg-elevated] rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm font-semibold text-[--text-primary]">Notifications</p>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button onClick={handleMarkAllRead} className="text-xs text-[#00C2A8] hover:underline">
                    Mark all read
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-[--text-secondary] hover:text-[--text-primary]">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-10 text-center text-sm text-[--text-secondary]">No notifications yet</div>
              ) : notifications.map(notif => {
                const Icon = NOTIF_ICONS[notif.type] || Info;
                const color = NOTIF_COLORS[notif.type] || 'text-gray-500';
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleNotifClick(notif)}
                    className={clsx(
                      'w-full flex items-start gap-3 px-4 py-3 hover:bg-[--bg-main] transition-colors text-left',
                      !notif.read && 'bg-[#00C2A8]/5'
                    )}
                  >
                    <Icon className={clsx('w-4 h-4 mt-0.5 shrink-0', color)} />
                    <div className="flex-1 min-w-0">
                      <p className={clsx('text-xs font-semibold truncate', notif.read ? 'text-[--text-secondary]' : 'text-[--text-primary]')}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-[--text-secondary] mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{relativeTime(notif.created_at)}</p>
                    </div>
                    {!notif.read && (
                      <div className="w-2 h-2 bg-[#00C2A8] rounded-full mt-1 shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
